// src/context/ActivaContext.tsx
import React from "react";
import { classifyActiva, normalizeValence, type ActivaState, type Features } from "@/engines/activaRuntime";

// Public shape
export type ActivaContextValue = {
  state: ActivaState;
  features: Features;
  setFeatures: (patch: Partial<Features>) => void;
  enableMock: (on: boolean) => void;
};

const Ctx = React.createContext<ActivaContextValue | null>(null);

const DEFAULT: Features = {
  valence: 0.55,
  arousal: 0.35,
  agency:  0.55,
  friction:0.25,
  coherence:0.60,
};

export function ActivaProvider({ children }: { children: React.ReactNode }) {
  const [features, setF] = React.useState<Features>(DEFAULT);
  const [state, setState] = React.useState<ActivaState>(classifyActiva(DEFAULT));
  const mockRef = React.useRef<number | null>(null);

  // recompute state on feature change
  React.useEffect(() => {
    setState(classifyActiva(features));
  }, [features]);

  // simple API
  const setFeatures = React.useCallback((patch: Partial<Features>) => {
    setF(prev => ({ ...prev, ...patch }));
  }, []);

  // optional mock stream to visualize global morphing
  const enableMock = React.useCallback((on: boolean) => {
    if (on) {
      if (mockRef.current) return;
      mockRef.current = window.setInterval(() => {
        setF(prev => {
          const t = Date.now() / 1000;
          // slow, smooth oscillations to demo states without being chaotic
          const valence   = normalizeValence(Math.sin(t * 0.12));
          const arousal   = 0.45 + 0.25 * Math.sin(t * 0.18 + 1.2);
          const agency    = 0.50 + 0.20 * Math.cos(t * 0.16 - 0.5);
          const friction  = 0.35 + 0.35 * Math.max(0, Math.sin(t * 0.11 - 1.3));
          const coherence = 0.55 + 0.30 * Math.cos(t * 0.10 + 0.8);
          return { ...prev, valence, arousal, agency, friction, coherence };
        });
      }, 1500);
    } else if (mockRef.current) {
      clearInterval(mockRef.current);
      mockRef.current = null;
      setF(DEFAULT);
    }
  }, []);

  const value: ActivaContextValue = React.useMemo(() => ({
    state, features, setFeatures, enableMock
  }), [state, features, setFeatures, enableMock]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useActiva() {
  const v = React.useContext(Ctx);
  if (!v) throw new Error("useActiva must be used within ActivaProvider");
  return v;
}
